<template>
  <v-container fluid class="ma-4 mt-3 px-8">
    <v-row justify="start" align="center">
      <v-col cols="12" md="3">
        <v-autocomplete
          v-model="form.payment_type"
          outlined
          dense
          hide-details="auto"
          :items="['Admission', 'Academic']"
          :rules="[rules.required]"
          label="Concession Type"
          clearable
        />
      </v-col>

      <v-col v-if="form.payment_type === 'Admission'" cols="12" md="3">
        <v-text-field
          v-model="form.application_id"
          outlined
          dense
          hide-details="auto"
          :rules="[rules.required, rules.applicationNumber]"
          maxlength="15"
          label="Application ID"
          clearable
        />
      </v-col>

      <v-col v-if="form.payment_type === 'Academic'" cols="12" md="3">
        <v-text-field
          v-model="form.usn_id"
          outlined
          dense
          hide-details="auto"
          :rules="[rules.required]"
          label="USN"
          clearable
        />
      </v-col>

      <v-col cols="12" md="3" class="d-flex justify-start">
        <v-btn
          class="accent white--text"
          :loading="loading"
          :disabled="loading || !form.payment_type"
          @click="search()"
        >
          Search
        </v-btn>
        <v-btn
          :disabled="!records.length"
          class="accent white--text ml-2"
          @click="download"
        >
          <v-icon>mdi-download</v-icon> Download
        </v-btn>
        <v-btn
          class="ml-2"
          outlined
          color="accent"
          :disabled="!form.payment_type"
          @click="bulkUploadDialog = true"
        >
          <v-icon left small>mdi-upload</v-icon>
          Upload
        </v-btn>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-sheet outlined>
          <v-data-table
            :headers="headers"
            :items="records"
            fixed-header
            :item-key="itemKey"
          >
            <template #[`item.action`]="{ item }">
              <v-icon
                small
                class="mr-2"
                color="accent"
                @click="openDialog(item)"
              >
                mdi-pencil
              </v-icon>
            </template>
            <template v-slot:[`item.partner_id`]="{ item }">
              {{ getPartnerName(item.partner_id) }}
            </template>
            <template v-slot:[`item.program_id`]="{ item }">
              {{ getProgramName(item.program_id) }}
            </template>
            <template v-slot:[`item.particulars`]="{ item }">
              <span
                v-for="(a, i) in parseParticulars(item.particulars).additions"
                :key="i"
              >
                {{ a.text }}: {{ a.value }}<br />
              </span>
            </template>
            <template v-slot:no-data>
              <div class="text-center">No data available</div>
            </template>
          </v-data-table>
        </v-sheet>
      </v-col>
    </v-row>

    <v-dialog
      v-model="discountDialog"
      v-if="discountDialog"
      width="850"
      persistent
      scrollable
    >
      <admissionDialog
        v-show="discountDialog && form.payment_type === 'Admission'"
        :data="selectedRecord"
        @closeDialog="discountDialog = false"
        @create="setAdmissionConcession"
        :loading="loading"
      />
      <academicDialog
        v-show="discountDialog && form.payment_type === 'Academic'"
        :data="selectedRecord"
        @closeDialog="discountDialog = false"
        @create="setAcademicConcession"
        :loading="loading"
      />
    </v-dialog>
    <v-dialog v-model="bulkUploadDialog" width="500" persistent scrollable>
      <bulkConcessionUpload
        v-if="bulkUploadDialog"
        :concession-type="form.payment_type"
        :loading="loading"
        @closeDialog="bulkUploadDialog = false"
        @upload="handleBulkUpload"
      />
    </v-dialog>

    <v-dialog v-model="uploadErrorDialog" width="450" persistent>
      <uploadError
        v-if="uploadErrorDialog"
        :errorCsvBlob="errorCsvBlob"
        @downloaded="
          search(), (uploadErrorDialog = false), (errorCsvBlob = null)
        "
        :csvTitle="`${form.payment_type} Concession`"
      />
    </v-dialog>
  </v-container>
</template>

<script>
import { rules } from "@/helpers/rules";
import {
  feeDuePendingHeaders,
  studentInvoiceConcessionHeaders,
} from "@/constants/headers";
import admissionDialog from "@/components/discount/admission.vue";
import academicDialog from "@/components/discount/academic.vue";
import bulkConcessionUpload from "@/components/discount/bulkUpload.vue";
import uploadError from "@/components/uploadError.vue";

export default {
  components: {
    admissionDialog,
    academicDialog,
    bulkConcessionUpload,
    uploadError,
  },

  data() {
    return {
      form: {},
      programs: [],
      partners: [],
      rules,
      loading: false,
      records: [],
      headers: [],
      discountDialog: false,
      selectedRecord: {},
      bulkUploadDialog: false,
      uploadErrorDialog: false,
      errorCsvBlob: null,
    };
  },

  computed: {
    isAcademic() {
      return this.form.payment_type === "Academic";
    },
    itemKey() {
      return this.isAcademic ? "id" : "application_id";
    },
  },

  watch: {
    "form.payment_type"(val) {
      this.records = [];
      if (val === "Admission") {
        this.headers = feeDuePendingHeaders.map((item) => {
          if (item.value !== "action") return item;
          return {
            text: "Action",
            value: "action",
            class: "black--text font-weight-bold secondary",
            sortable: false,
          };
        });
      } else {
        this.headers = studentInvoiceConcessionHeaders.map((item) => {
          if (item.value !== "action") return item;
          return {
            text: "Action",
            value: "action",
            class: "black--text font-weight-bold secondary",
            sortable: false,
          };
        });
      }
    },
  },

  async created() {
    await this.getPrograms();
    await this.getPartners();
  },

  methods: {
    search() {
      if (this.form.payment_type === "Admission") {
        this.getAdmissionDue();
      } else {
        this.getStudentInvoices();
      }
    },

    openDialog(item) {
      this.selectedRecord = item;
      this.discountDialog = true;
    },

    parseParticulars(raw) {
      const empty = { additions: [], subtractions: [] };
      if (!raw) return empty;
      try {
        return typeof raw === "string" ? JSON.parse(raw) : raw;
      } catch {
        return empty;
      }
    },

    async getAdmissionDue() {
      delete this.form.usn_id;
      const queryString = this.objectToQueryParams({ ...this.form });
      this.loading = true;
      try {
        const response = await this.$axios.get(
          `/student_admit_plans?${queryString}&!plan_amount_balance=0`,
        );
        this.records = response.data.data;
      } catch (e) {
        console.log(e);
      } finally {
        this.loading = false;
      }
    },

    async setAdmissionConcession(item) {
      this.loading = true;
      try {
        await this.$axios.post("/set_admission_concession", {
          application_id: item.application_id,
          amount: item.concession_amount,
          remarks: item.remarks,
        });
        this.$store.commit("showSnackbar", {
          message: "Admission concession applied successfully",
          color: "success",
        });
        this.discountDialog = false;
        await this.getAdmissionDue();
      } catch (error) {
        const errorMessage = await this.$store.dispatch(
          "throwError",
          error.response,
        );
        this.$store.commit("showSnackbar", {
          message: errorMessage,
          color: "error",
        });
      } finally {
        this.loading = false;
      }
    },

    async getStudentInvoices() {
      delete this.form.application_id;

      const queryString = this.objectToQueryParams({
        usn_id: this.form.usn_id,
      });
      this.loading = true;
      try {
        const response = await this.$axios.get(
          `/list_student_invoices_report?${queryString}&status=Pending&types=Term Fee,Annual`,
        );
        this.records = response.data.data;
      } catch (e) {
        console.log(e);
      } finally {
        this.loading = false;
      }
    },

    async setAcademicConcession(item) {
      this.loading = true;
      try {
        await this.$axios.post("/set_academic_concession", {
          student_invoice_id: item.invoice_id,
          concession_amount: item.concession_amount,
          remarks: item.remarks,
        });
        this.$store.commit("showSnackbar", {
          message: "Academic concession applied successfully",
          color: "success",
        });
        this.discountDialog = false;
        await this.getStudentInvoices();
      } catch (error) {
        const errorMessage = await this.$store.dispatch(
          "throwError",
          error.response,
        );
        this.$store.commit("showSnackbar", {
          message: errorMessage,
          color: "error",
        });
      } finally {
        this.loading = false;
      }
    },

    async download() {
      const queryString = this.objectToQueryParams(this.form);
      this.$axios
        .get(
          `/download_bulk_payments?${queryString}&header=Concession Amount&status=Pending&types=Term Fee,Annual`,
          {
            responseType: "blob",
          },
        )
        .then((res) => {
          let blob = new Blob([res.data], { type: "text/csv" }),
            url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute(
            "download",
            `${this.form.payment_type} Payments.csv`,
          );
          document.body.appendChild(link);
          link.click();
        });
    },

    objectToQueryParams(obj) {
      return Object.keys(obj)
        .filter(
          (key) =>
            obj[key] !== undefined && obj[key] !== null && obj[key] !== "",
        )
        .map(
          (key) => encodeURIComponent(key) + "=" + encodeURIComponent(obj[key]),
        )
        .join("&");
    },

    async getPrograms() {
      try {
        const response = await this.$axios.get("/programs");
        this.programs = response.data.data;
      } catch (error) {
        console.log(error);
      }
    },
    getProgramName(id) {
      const program = this.programs.find((p) => p.id === id);
      return program
        ? program.degree.short_name +
            (program.specialization ? " - " + program.specialization : "")
        : "";
    },
    async getPartners() {
      try {
        const response = await this.$axios.get("/partners");
        this.partners = response.data.data;
      } catch (error) {
        console.log(error);
      }
    },
    getPartnerName(id) {
      const partner = this.partners.find((p) => p.id === id);
      return partner ? partner.name : "";
    },
    async handleBulkUpload(file, remarks) {
      this.loading = true;
      const formData = new FormData();
      formData.append("csv_file", file, file.name);
      formData.append("payment_type", this.form.payment_type);
      formData.append("remarks", remarks);

      await this.$axios
        .post("/upload_bulk_concession_requests", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        })
        .then((response) => {
          this.loading = false;

          // 201 = partial success, errors CSV returned
          if (response.status === 201) {
            this.errorCsvBlob = new Blob([response.data]);
            this.uploadErrorDialog = true;
            this.bulkUploadDialog = false;
            return;
          }

          this.$store.commit("showSnackbar", {
            message: "Bulk concession applied successfully",
            color: "success",
          });
          this.bulkUploadDialog = false;
          this.search();
        })
        .catch(async (error) => {
          this.loading = false;
          const errorMessage = await this.$store.dispatch(
            "throwError",
            error.response,
          );
          this.$store.commit("showSnackbar", {
            message: errorMessage,
            color: "error",
          });
        });
    },
  },
};
</script>

<style scoped></style>
